import { useState, useRef, useCallback } from 'react';

import { AudioVisualizerData } from '../types';

export const useAudioStream = () => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [audioData, setAudioData] = useState<AudioVisualizerData>({ frequencies: [], volume: 0 });
  
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  
  const startStream = useCallback(async (onAudioChunk: (chunk: Float32Array) => void) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { 
          sampleRate: 48000,
          channelCount: 1,
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        } 
      });
      
      streamRef.current = stream;
      
      const audioContext = new AudioContext({ sampleRate: 48000 });
      audioContextRef.current = audioContext;
      
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      
      analyser.fftSize = 256;
      analyserRef.current = analyser;
      processorRef.current = processor;
      
      source.connect(analyser);
      analyser.connect(processor);
      processor.connect(audioContext.destination);
      
      processor.onaudioprocess = (event) => {
        const inputBuffer = event.inputBuffer;
        const inputData = inputBuffer.getChannelData(0);
        
        // Send audio chunk for detection
        onAudioChunk(inputData);
        
        // Update visualizer data
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyser.getByteFrequencyData(dataArray);
        
        const volume = inputData.reduce((sum, val) => sum + Math.abs(val), 0) / inputData.length;
        
        setAudioData({
          frequencies: Array.from(dataArray),
          volume: volume * 100
        });
      };
      
      setIsStreaming(true);
      return true;
    } catch (error) {
      console.error('Error starting audio stream:', error);
      return false;
    }
  }, []);
  
  const stopStream = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.disconnect();
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    
    setIsStreaming(false);
    setAudioData({ frequencies: [], volume: 0 });
  }, []);
  
  return {
    isStreaming,
    audioData,
    startStream,
    stopStream
  };
};